import {
	getCollectionProps,
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { useId } from 'react'
import {
	type ActionFunctionArgs,
	data,
	type MetaFunction,
	useFetcher,
} from 'react-router'
import { z } from 'zod'
import { CheckboxField, Field, TextareaField } from '#app/components/forms.js'
import { StatusButton } from '#app/components/ui/status-button.js'
import { sendEmail } from '#app/utils/email.server.js'
import { useIsPending } from '#app/utils/misc.js'
import { redirectWithToast } from '#app/utils/toast.server.js'
import { useOptionalUser } from '#app/utils/user.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{ route: '/legal-notice/data-subject-access-request', priority: 0.7 },
	],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Cookie Policy | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Cookie Policy for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

const schema = z.object({
	name: z.string().min(1, 'Name is required'),
	email: z.string().email('Invalid email address'),
	request_as: z.enum(['personal', 'agent']),
	compliance_type: z.enum([
		'gdpr',
		'ccpa',
		'cpa',
		'ctdpa',
		'ucpa',
		'vcdpa',
		'other',
	]),
	action: z.enum([
		'request_to_know',
		'request_to_delete',
		'request_to_opt_out',
		'request_to_opt_in',
		'access_personal_info',
		'fix_inaccurate_info',
		'receive_a_copy_of_info',
		'opt_out_cross_context_advertising',
		'limit_sensitive_personal_info',
		'specify_comment',
	]),
	content: z.string().optional(),
	declare_information: z
		.boolean()
		.refine((val) => val, 'You must declare the information to be true'),
	understand_deletion: z
		.boolean()
		.refine((val) => val, 'You must understand the deletion consequences'),
	validate_email: z
		.boolean()
		.refine((val) => val, 'You must agree to validate your email'),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.error },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const textMessage = `${import.meta.env.VITE_APP_TITLE} Data Subject Access Request
	Data:  ${JSON.stringify(submission.value, null, 2)}
	`
	const htmlMessage = `<h1>${import.meta.env.VITE_APP_TITLE} Data Subject Access Request</h1><br/>
	Data:  ${JSON.stringify(submission.value, null, 2)}<br/>
	`
	const response = await sendEmail({
		to: process.env.REPORT_ISSUE_EMAIL_RECEIVER,
		subject: `${import.meta.env.VITE_APP_TITLE} - Data Subject Access Request from ${submission.value.email}`,
		text: textMessage,
		html: htmlMessage,
	})

	if (response.status === 'success') {
		return redirectWithToast('/legal-notice/terms-of-service', {
			description:
				'Your request has been submitted. We will get back to you as soon as possible.',
		})
	}

	return data({ result: submission.payload }, response.error.statusCode)
}

export default function DataSubjectAccessRequestRoute() {
	const user = useOptionalUser()
	const isPending = useIsPending()

	const fetcher = useFetcher<typeof action>()
	const id = useId()
	const formId = `${id}-data-subject-access-request-${user?.id || 'unknown'}`

	const [form, fields] = useForm({
		id: formId,
		defaultValue: {
			name: user?.name ?? '',
			email: user?.email ?? '',
			request_as: 'personal',
			compliance_type: '',
			action: '',
			content: '',
			declare_information: false,
			understand_deletion: false,
			validate_email: false,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<main className="mx-auto max-w-3xl p-6">
			<h1 className="mb-6 text-3xl font-bold">Data Subject Access Request</h1>
			<p className="mb-6">
				<strong>Last updated:</strong> <strong>October 6, 2025</strong>
			</p>
			<fetcher.Form method="post" {...getFormProps(form)}>
				<Field
					labelProps={{
						htmlFor: fields.name.id,
						children: 'Your Name',
						className: 'block text-sm md:text-lg font-medium text-gray-700',
					}}
					inputProps={{
						...getInputProps(fields.name, { type: 'text' }),
						defaultValue: user?.name || '',
						className:
							'mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm',
					}}
					errors={fields.name.errors}
				/>

				<Field
					labelProps={{
						htmlFor: fields.email.id,
						children: 'What email address do you use to access this website?',
						className: 'block text-sm font-medium text-gray-700',
					}}
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						defaultValue: user?.email || '',
						className:
							'mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm',
					}}
					errors={fields.email.errors}
				/>

				<fieldset>
					<legend className="mb-2 block text-sm font-medium text-gray-700">
						You are submitting this request as:
					</legend>
					<div className="space-y-2">
						{getCollectionProps(fields.request_as, {
							type: 'radio',
							options: [
								'The person, or the parent / guardian of the person, whose name appears above.',
								'An agent authorized by the consumer to make this request on their behalf.',
							],
						}).map((props) => (
							<label
								key={props.id}
								htmlFor={props.id}
								className="flex items-center"
							>
								<input
									{...props}
									className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 block text-sm text-gray-900">
									{props.value}
								</span>
							</label>
						))}
					</div>
					{fields.request_as.errors &&
						fields.request_as.errors.map((error) => (
							<p key={error} className="mt-2 text-sm text-red-600">
								{error}
							</p>
						))}
				</fieldset>

				<div className="py-4">
					<label
						htmlFor={fields.compliance_type.id}
						className="block pt-2 text-sm font-medium text-gray-700"
					>
						Under the rights of which law are you making this request?
					</label>
					<select
						{...getSelectProps(fields.compliance_type)}
						className="mt-1 block w-full rounded-md border-gray-300 py-2 pr-10 pl-3 text-base focus:border-indigo-500 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
					>
						<option value="">Please select a law</option>
						{['gdpr', 'ccpa', 'cpa', 'ctdpa', 'ucpa', 'vcdpa', 'other'].map(
							(value) => (
								<option key={value} value={value}>
									{value.toUpperCase()}
								</option>
							),
						)}
					</select>
					{fields.compliance_type.errors &&
						fields.compliance_type.errors.map((error) => (
							<p key={error} className="mt-2 text-sm text-red-600">
								{error}
							</p>
						))}
				</div>

				<fieldset>
					<legend className="mb-2 block text-sm font-medium text-gray-700">
						I am submitting a request to:
					</legend>
					<div className="space-y-2">
						{getCollectionProps(fields.action, {
							type: 'radio',
							options: [
								'Know what information is being collected from me',
								'Have my information deleted',
								'Opt out of having my data sold to third parties',
								'Opt in to the sale of my personal data',
								'Access my personal information',
								'Fix inaccurate information',
								'Receive a copy of my personal information',
								'Opt out of having my data shared for cross-context behavioral advertising',
								'Limit the use and disclosure of my sensitive personal information',
								'Other (please specify in the comment box below)',
							],
						}).map((props) => (
							<label key={props.id} className="flex items-center">
								<input
									{...props}
									className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 block text-sm text-gray-900">
									{props.value}
								</span>
							</label>
						))}
					</div>
					{fields.action.errors &&
						fields.action.errors.map((error) => (
							<p key={error} className="mt-2 text-sm text-red-600">
								{error}
							</p>
						))}
				</fieldset>

				<TextareaField
					labelProps={{
						htmlFor: fields.content.id,
						children:
							'Please leave details regarding your action request or question.',
						className: 'pt-4 block text-sm font-medium text-gray-700',
					}}
					textareaProps={{
						...getTextareaProps(fields.content),
						className:
							'mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm',
						rows: 4,
					}}
					errors={fields.content.errors}
				/>

				<fieldset>
					<legend className="block text-sm font-medium text-gray-700">
						I confirm that:
					</legend>
					<div className="space-y-2">
						{[
							{
								field: fields.declare_information,
								label:
									'Under penalty of perjury, I declare all the above information to be true and accurate.',
							},
							{
								field: fields.understand_deletion,
								label:
									'I understand that the deletion or restriction of my personal data is irreversible and may result in the termination of services with My Great New Website / App.',
							},
							{
								field: fields.validate_email,
								label:
									'I understand that I will be required to validate my request by email, and I may be contacted in order to complete the request.',
							},
						].map(({ field, label }) => (
							<CheckboxField
								key={id}
								labelProps={{
									htmlFor: field.id,
									children: label,
									className: 'ml-2 block text-sm text-gray-900',
								}}
								buttonProps={{
									...getInputProps(field, { type: 'checkbox' }),
									className:
										'mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500',
								}}
								errors={field.errors}
							/>
						))}
					</div>
				</fieldset>

				<div>
					<StatusButton
						className="my-4"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						form={formId}
					>
						SUBMIT
					</StatusButton>
				</div>
			</fetcher.Form>
		</main>
	)
}
